export interface IRoute {
  distance: number;
  duration: number;
  geometry: {
    coordinates: [number, number][];
    type: 'LineString';
  };
  legs: {
    // The distance traveled between waypoints, in meters.
    distance: number;
    // The estimated travel time between waypoints, in seconds.
    duration: number;
  }[];
}

/**
 * Serializes coordinates to a string format suitable for Mapbox API
 * example: serializeCoordinates([4.9041, 52.3676]) // "4.904,52.368"
 * see https://docs.mapbox.com/api/navigation/directions/#directions-formats
 * @param coordinates Coordinates as a tuple of [longitude, latitude]
 * @returns string representation of coordinates

 */
export function serializeCoordinates(coordinates: [number, number]) {
  return coordinates.map((c) => c.toFixed(3)).join(',');
}

/**
 * Create a route between multiple points
 * @param token MAPBOX API token
 * @param points Array of [longitude, latitude] pairs. Max 25 points.
 * @returns The route or null if no route was found
 */
export async function createRoute(token: string, points: Array<[number, number]>): Promise<IRoute | null> {
  if (points.length < 2) {
    throw new Error('At least two points are required to create a route');
  }

  if (points.length > 25) {
    throw new Error('A maximum of 25 points is allowed to create a route');
  }

  const serializedPoints = points.map((p) => serializeCoordinates(p)).join(';');
  const searchParams = new URLSearchParams();
  searchParams.set('access_token', token);
  searchParams.set('language', 'nl');
  searchParams.set('geometries', 'geojson');
  searchParams.set('overview', 'full');

  const result = await fetch(
    `https://api.mapbox.com/directions/v5/mapbox/driving/${serializedPoints}.json?${searchParams.toString()}`,
    {
      headers: {
        accept: 'application/json',
      },
    },
  );
  const json = await result.json();
  if (!result.ok) {
    throw new Error('No results found');
  }
  return json.routes[0] ?? null;
}

/**
 * Convert a place name or postal code to coordinates and a name using the Mapbox Geocoding API.
 * See https://docs.mapbox.com/api/search/geocoding/#forward-geocoding-with-search-text-input.
 * @param token MAPBOX API token
 * @param country Country, in ISO 3166-1 alpha-2 format (e.g., 'NL' for the Netherlands)
 * @param location Location string to search for (e.g., 'Amsterdam')
 * @throws Error if the request fails
 * @returns An object with {longitude, latitude, name} or null if not found
 */
export async function geoCodeLocationString(
  token: string,
  country: string,
  location: string,
  permanent: boolean = true,
): Promise<{ longitude: number; latitude: number; fullName: string } | null> {
  const searchParams = new URLSearchParams();
  searchParams.set('access_token', token);
  searchParams.set('language', 'nl');
  searchParams.set('q', location);
  searchParams.set('country', country);
  searchParams.set('permanent', permanent ? 'true' : 'false');
  searchParams.set('autocomplete', 'false');
  searchParams.set('limit', '1');

  console.info(`Fetching Mapbox coordinates for location "${location}" in country "${country}"`);
  const result = await fetch(`https://api.mapbox.com/search/geocode/v6/forward?${searchParams.toString()}`, {
    headers: {
      accept: 'application/json',
    },
  });
  if (!result.ok) {
    throw new Error(`Error fetching coordinates for location "${location}": HTTP ${result.status} ${result.text}`);
  }

  const json = await result.json();

  if (!json.features || json.features.length === 0) {
    console.debug(`No coordinates found for location "${location}" in country "${country}"`);
    return null;
  }

  const feature = json.features[0];

  // For address-level matches, Mapbox includes a match_code describing which parts of the
  // query it actually matched. It will still return its single best guess even when the city
  // doesn't match at all (e.g. "Route Départementale 396" is a road name reused across many
  // French départements) - silently accepting that guess plants a geocode hundreds of km off,
  // which corrupts route distances. Treat an unmatched place/city as no result so the caller
  // falls back to a less specific (but reliable) representation, e.g. city + postal code.
  const matchCode = feature.properties.match_code;
  if (matchCode?.place === 'unmatched') {
    console.debug(
      `Rejecting low-confidence geocode match for "${location}" in country "${country}": ${JSON.stringify(matchCode)}`,
    );
    return null;
  }

  const coordinates = feature.properties.coordinates;
  return {
    longitude: coordinates.longitude,
    latitude: coordinates.latitude,
    fullName: feature.properties.full_address,
  };
}

/**
 * Convert a Location object to coordinates and a name using the Mapbox Geocoding API.
 * See https://docs.mapbox.com/api/search/geocoding/#forward-geocoding-with-search-text-input.
 * @param token MAPBOX API token
 * @param location Location object
 * @throws Error if the request fails
 * @returns An object with {longitude, latitude, name} or null if not found
 */
export async function geoCodeLocation(
  token: string,
  location: {
    streetNumber: string;
    street: string;
    city: string;
    postalCode: string;
    country: string;
  },
  permanent: boolean = true,
): Promise<{ longitude: number; latitude: number; fullName: string } | null> {
  if (!location.country || !location.postalCode) {
    throw new Error('Location must have a country and postal code');
  }

  // Deliberately no bare `location.city` representation: many town names (e.g. "Marolles")
  // exist multiple times in the same country, and a city-only match carries no match_code/
  // confidence signal to catch a wrong pick - it silently resolves to an unrelated place. The
  // postal code anchors every remaining representation to the right area instead.
  const representations = [
    `${location.streetNumber} ${location.street} ${location.city} ${location.postalCode}`,
    `${location.city}, ${location.postalCode}`,
    `${location.city}, ${location.postalCode.replace(/\s+/g, '')}`,
    location.postalCode,
    location.postalCode.replace(/\s+/g, ''),
  ];

  for (const representation of representations) {
    const place = await geoCodeLocationString(token, location.country.toUpperCase(), representation, permanent);
    if (place) {
      return place;
    }
  }

  return null;
}

export interface IReverseGeocode {
  /** The city/locality the coordinates sit in, or null when Mapbox has none for this point. */
  city: string | null;
  /** ISO 3166-1 alpha-2, upper-cased. A fallback for payloads that omit a country. */
  countryCode: string | null;
}

/**
 * Convert coordinates to the city they sit in using the Mapbox reverse Geocoding API.
 * See https://docs.mapbox.com/api/search/geocoding/#reverse-geocoding.
 *
 * Restricted to city-level results on purpose: this feeds a truck's "currently in <city>" card, and an
 * unrestricted reverse lookup would answer with the street the truck is parked on.
 *
 * @param token MAPBOX API token
 * @param latitude Latitude of the point
 * @param longitude Longitude of the point
 * @throws Error if the request fails
 * @returns The city and country, or null if Mapbox has no city-level result for the point
 */
export async function reverseGeoCodeCity(
  token: string,
  latitude: number,
  longitude: number,
  permanent: boolean = true,
): Promise<IReverseGeocode | null> {
  const searchParams = new URLSearchParams();
  searchParams.set('access_token', token);
  searchParams.set('language', 'nl');
  searchParams.set('longitude', String(longitude));
  searchParams.set('latitude', String(latitude));
  searchParams.set('types', 'place,locality');
  searchParams.set('permanent', permanent ? 'true' : 'false');

  const result = await fetch(`https://api.mapbox.com/search/geocode/v6/reverse?${searchParams.toString()}`, {
    headers: {
      accept: 'application/json',
    },
  });

  if (!result.ok) {
    throw new Error(`Error reverse geocoding ${latitude},${longitude}: HTTP ${result.status}`);
  }

  const json = await result.json();
  const feature = json.features?.[0];
  if (!feature) {
    console.debug(`No city found for coordinates ${latitude},${longitude}`);
    return null;
  }

  const properties = feature.properties;
  const countryCode: unknown = properties.context?.country?.country_code;

  return {
    city: properties.context?.place?.name ?? properties.name ?? properties.context?.locality?.name ?? null,
    countryCode: typeof countryCode === 'string' ? countryCode.toUpperCase() : null,
  };
}
